import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/services/api";
import { useSession } from "hooks/useSession";
import type { ScimGroupMapping, UpdateScimGroupMappingRequest } from "@/types";

const SCIM_GROUPS_QUERY_KEY = "scim-groups";

const scimUrl = (orgId: string) => `/api/organizations/${orgId}/scim`;

const useOrgId = () => {
  const { currentOrganization } = useSession();
  return currentOrganization?.id;
};

export const useScimGroupMappings = () => {
  const orgId = useOrgId();

  return useQuery({
    queryKey: [SCIM_GROUPS_QUERY_KEY, orgId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error("No active organization");
      }
      const response = await axiosInstance.get<ScimGroupMapping[]>(
        `${scimUrl(orgId)}/groups`
      );
      return response.data;
    },
    enabled: Boolean(orgId),
  });
};

type UpdateScimGroupMappingVariables = {
  groupId: string;
  values: UpdateScimGroupMappingRequest;
};

/** Replaces the whole access of a group: anything not sent is revoked. */
export const useUpdateScimGroupMapping = () => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation<
    ScimGroupMapping,
    unknown,
    UpdateScimGroupMappingVariables
  >({
    mutationFn: async ({ groupId, values }) => {
      if (!orgId) {
        throw new Error("No active organization");
      }
      const response = await axiosInstance.put<ScimGroupMapping>(
        `${scimUrl(orgId)}/groups/${groupId}`,
        values
      );
      return response.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SCIM_GROUPS_QUERY_KEY, orgId],
      }),
  });
};

/** Re-syncs the whole directory; the backend answers 202 right away. */
export const useSyncScimDirectory = () => {
  const orgId = useOrgId();

  return useMutation<void, unknown>({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error("No active organization");
      }
      await axiosInstance.post(`${scimUrl(orgId)}/sync`);
    },
  });
};
